import { Controller } from "../../lib/stimulus-shim"

export default class extends Controller {
  static targets = ["popup", "customizePanel", "categoryCheckbox"]
  static values = { 
    consentDuration: { type: Number, default: 13 }, // mois
    categories: Array
  }

  connect() {
    this.checkConsentStatus()

    // Écouter les changements de visibilité de la page pour re-vérifier le consentement
    document.addEventListener('visibilitychange', () => {
      if (!document.hidden) {
        this.checkConsentStatus()
      }
    })
  }

  checkConsentStatus() {
    const consent = this.getStoredConsent()

    // Mémoriser l'état marketing actuel pour détecter les changements
    if (consent && consent.marketing !== undefined) {
      this._previousMarketingConsent = consent.marketing
    }

    if (!consent) {
      this.showConsentPopup()
    } else if (this.isConsentExpired(consent)) {
      this.showConsentPopup()
    } else {
      this.hideConsentPopup()
      this.applyConsentSettings(consent)
    }
  }

  getStoredConsent() {
    const stored = localStorage.getItem('rgpd-consent')
    try {
      return stored ? JSON.parse(stored) : null
    } catch (error) {
      console.error('Error parsing stored consent:', error)
      return null
    }
  }

  isConsentExpired(consent) {
    if (!consent.timestamp) return true
    
    const consentDate = new Date(consent.timestamp)
    const expiryDate = new Date(consentDate)
    expiryDate.setMonth(expiryDate.getMonth() + this.consentDurationValue)
    
    return new Date() > expiryDate
  }

  showConsentPopup() {
    console.log("Attempting to show RGPD popup")
    console.log("Has popup target:", this.hasPopupTarget)
    
    if (this.hasPopupTarget) {
      console.log("Showing RGPD popup")
      
      // Retirer le hidden et forcer le reflow
      this.popupTarget.classList.remove('hidden')
      this.popupTarget.offsetHeight // Force reflow
      
      // Bloquer le scroll arrière-plan
      document.body.classList.add('overflow-hidden')

      // Ajouter les classes d'animation
      this.popupTarget.classList.add('rgpd-popup-show')
      
      // Animation de la carte popup
      const popupCard = this.popupTarget.querySelector('.w-full.max-w-2xl')
      if (popupCard) {
        popupCard.classList.add('rgpd-card-show')
      }
    } else {
      console.error("RGPD popup target not found!")
    }
  }

  hideConsentPopup() {
    if (this.hasPopupTarget) {
      // Animation de sortie
      this.popupTarget.classList.remove('rgpd-popup-show')
      this.popupTarget.classList.add('rgpd-popup-hide')
      
      const popupCard = this.popupTarget.querySelector('.w-full.max-w-2xl')
      if (popupCard) {
        popupCard.classList.remove('rgpd-card-show')
        popupCard.classList.add('rgpd-card-hide')
      }
      
      // Masquer complètement après l'animation
      setTimeout(() => {
        this.popupTarget.classList.add('hidden')
        this.popupTarget.classList.remove('rgpd-popup-hide')
        document.body.classList.remove('overflow-hidden')
        if (popupCard) {
          popupCard.classList.remove('rgpd-card-hide')
        }
      }, 300) // Durée de l'animation
    }
  }

  acceptAll(event) {
    event.preventDefault()
    console.log("User accepted all cookies")

    const consent = { essential: true, timestamp: new Date().toISOString(), version: '1.0' }
    this.categoryCheckboxTargets.forEach(cb => { consent[cb.dataset.category] = true })

    this.saveConsent(consent)
    this.applyConsentSettings(consent)
    this.hideConsentPopup()
    this.trackConsentEvent('accept_all')
  }

  refuseAll(event) {
    event.preventDefault()
    console.log("User refused all non-essential cookies")

    const consent = { essential: true, timestamp: new Date().toISOString(), version: '1.0' }
    this.categoryCheckboxTargets.forEach(cb => {
      consent[cb.dataset.category] = cb.dataset.category === 'essential'
    })

    this.saveConsent(consent)
    this.applyConsentSettings(consent)
    this.hideConsentPopup()
    this.trackConsentEvent('refuse_all')
  }

  showCustomizePanel(event) {
    event.preventDefault()
    
    if (this.hasCustomizePanelTarget) {
      this.customizePanelTarget.classList.remove('hidden')
      this.customizePanelTarget.offsetHeight // Force reflow
      this.customizePanelTarget.classList.add('rgpd-panel-show')
      
      // Pré-cocher les catégories selon les préférences existantes
      this.loadCurrentPreferences()
    }
  }

  hideCustomizePanel(event) {
    event.preventDefault()
    
    if (this.hasCustomizePanelTarget) {
      this.customizePanelTarget.classList.remove('rgpd-panel-show')
      this.customizePanelTarget.classList.add('rgpd-panel-hide')
      
      setTimeout(() => {
        this.customizePanelTarget.classList.add('hidden')
        this.customizePanelTarget.classList.remove('rgpd-panel-hide')
      }, 250)
    }
  }

  loadCurrentPreferences() {
    const currentConsent = this.getStoredConsent()
    
    this.categoryCheckboxTargets.forEach(checkbox => {
      const category = checkbox.dataset.category
      if (currentConsent && currentConsent[category] !== undefined) {
        checkbox.checked = currentConsent[category]
      } else {
        // Valeurs par défaut : essentiels activés, autres désactivés
        checkbox.checked = category === 'essential'
      }
      
      // Désactiver les cookies essentiels (non modifiables)
      if (category === 'essential') {
        checkbox.disabled = true
      }
    })
  }

  saveCustomPreferences(event) {
    event.preventDefault()
    console.log("Saving custom preferences")
    
    const consent = {
      essential: true, // Toujours true
      preferences: false,
      timestamp: new Date().toISOString(),
      version: '1.0'
    }
    
    // Récupérer les préférences depuis les checkboxes
    this.categoryCheckboxTargets.forEach(checkbox => {
      const category = checkbox.dataset.category
      if (category !== 'essential') {
        consent[category] = checkbox.checked
      }
    })
    
    this.saveConsent(consent)
    this.applyConsentSettings(consent)
    this.hideConsentPopup()
    this.trackConsentEvent('custom', consent)
  }

  saveConsent(consent) {
    try {
      localStorage.setItem('rgpd-consent', JSON.stringify(consent))
      console.log('Consent saved:', consent)
    } catch (error) {
      console.error('Error saving consent:', error)
    }
  }

  applyConsentSettings(consent) {
    // Ahoy : cookies fonctionnels, toujours actifs (IPs anonymisées serveur)
    // Gérer les autres catégories selon les besoins
    this.applyPreferencesSettings(consent.preferences)

    // Marketing : reload si la valeur a changé (les pixels s'exécutent au chargement)
    if (consent.marketing !== undefined) {
      const previous = this._previousMarketingConsent
      if (previous !== undefined && previous !== consent.marketing) {
        window.location.reload()
        return
      }
      this._previousMarketingConsent = consent.marketing
    }

    // Dispatcher un événement personnalisé pour informer d'autres contrôleurs
    this.dispatch('consentUpdated', {
      detail: { consent: consent },
      bubbles: true
    })
  }

  applyPreferencesSettings(enabled) {
    // Google Fonts : activer/désactiver via media attribute
    document.querySelectorAll('link[data-rgpd-font]').forEach(link => {
      link.media = enabled ? 'all' : 'none'
    })
  }

  deleteCookie(name) {
    document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/; domain=.${window.location.hostname}`
    document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/`
  }

  trackConsentEvent(action, details = {}) {
    // Tracker l'événement de consentement (toujours autorisé car nécessaire)
    if (window.ahoy) {
      window.ahoy.track('rgpd_consent', {
        action: action,
        details: details,
        timestamp: new Date().toISOString()
      })
    }
  }

  // Méthode pour révoquer le consentement (appelable depuis les pages légales)
  revokeConsent() {
    localStorage.removeItem('rgpd-consent')
    this.showConsentPopup()
    console.log('Consent revoked')
  }

  // Méthodes utilitaires pour développeurs/debug
  debugConsent() {
    const consent = this.getStoredConsent()
    console.log('Current consent:', consent)
    console.log('Is expired:', consent ? this.isConsentExpired(consent) : 'No consent stored')
  }

  // Méthode pour forcer la réapparition de la popup (utile pour les tests)
  resetConsent() {
    localStorage.removeItem('rgpd-consent')
    this.checkConsentStatus()
  }

  // Méthode pour forcer l'affichage de la popup (debug)
  forceShow() {
    console.log("Force showing RGPD popup")
    this.showConsentPopup()
  }
}